/**
 * Core types for hermit-μDC Configurator
 */

// ============================================
// FORM FACTOR TYPES
// ============================================

export type FormFactor = 'rack-18' | 'rack-42' | 'desktop' | 'container' | 'embedded';

export interface FormFactorDefinition {
  id: FormFactor;
  name: string;
  description: string;
  recommended: boolean;
  hardwareFilters: {
    allowRackMount: boolean;
    allowDesktop: boolean;
    allowIndustrial: boolean;
    allowSBC: boolean;
    maxPowerWatts?: number;
  };
}

// ============================================
// GPU TYPES
// ============================================

export type GpuVendor = 'nvidia' | 'amd' | 'intel' | 'none';

export type GpuTier = 'entry' | 'desktop' | 'workstation' | 'datacenter' | 'edge';

export interface GpuSpecs {
  vendor: GpuVendor;
  model: string;
  vramGB: number;
  tdpWatts: number;
  tier: GpuTier;
  computeCapability?: string; // NVIDIA CUDA compute capability
  rocmSupport?: boolean;      // AMD ROCm support
  price: number;
}

// ============================================
// WIZARD STEP TYPES
// ============================================

export type WizardStep =
  | 'scenario'
  | 'network'
  | 'nodes'
  | 'services'
  | 'security'
  | 'summary';

export const WIZARD_STEPS: WizardStep[] = [
  'scenario',
  'network',
  'nodes',
  'services',
  'security',
  'summary',
];

export const STEP_LABELS: Record<WizardStep, string> = {
  scenario: 'Scenario',
  network: 'Network',
  nodes: 'Nodes',
  services: 'Services',
  security: 'Security',
  summary: 'Summary',
};

// ============================================
// SCENARIO TYPES
// ============================================

export type ScenarioType =
  | 'minimal'
  | 'lab-bench'
  | 'research'
  | 'industrial'
  | 'gdpr-llm'
  | 'ai-rendering'
  | 'lab-testing'
  | 'custom';

export interface ScenarioPreset {
  id: ScenarioType;
  name: string;
  description: string;
  nodeCount: number;
  estimatedCost: number;
  estimatedPower: number;
  useCases: string[];
  defaultNodes: NodePreset[];
}

// ============================================
// NETWORK TYPES
// ============================================

export interface NetworkConfig {
  subnet: string;
  gateway: string;
  netmask: string;
  domain: string;
  dhcpRangeStart: string;
  dhcpRangeEnd: string;
  vlans: VlanConfig[];
  // Multi-network support
  segments: NetworkSegment[];
  nodeAssignments: NodeNetworkAssignment[];
  subscriptionModel: SubscriptionModel;
}

export interface VlanConfig {
  id: number;
  name: string;
  subnet: string;
  purpose: string;
}

// ============================================
// NETWORK SEGMENT TYPES (Multi-network support)
// ============================================

export type NetworkSegmentType = 'administration' | 'user' | 'monitoring';

export type NetworkAccessLevel = 'provider' | 'customer' | 'shared';

export interface NetworkSegment {
  id: string;
  type: NetworkSegmentType;
  name: string;
  subnet: string;
  vlanId?: number;
  gateway: string;
  description: string;
  accessLevel: NetworkAccessLevel;
  color: string;
  enabled: boolean;
}

export interface NodeNetworkAssignment {
  nodeId: string;
  segmentId: string;
  ip: string;
  isPrimary: boolean;
}

export interface SubscriptionModel {
  enabled: boolean;
  providerName?: string;
  customerName?: string;
}

export const NETWORK_SEGMENT_PRESETS: Record<NetworkSegmentType, Omit<NetworkSegment, 'id'>> = {
  administration: {
    type: 'administration',
    name: 'Administration',
    subnet: '192.168.200.0/24',
    vlanId: 200,
    gateway: '192.168.200.1',
    description: 'Provider-managed administration and provisioning',
    accessLevel: 'provider',
    color: '#f97316', // Orange
    enabled: true,
  },
  user: {
    type: 'user',
    name: 'User Data',
    subnet: '192.168.201.0/24',
    vlanId: 201,
    gateway: '192.168.201.1',
    description: 'Customer-owned data network - provider has no access',
    accessLevel: 'customer',
    color: '#3b82f6', // Blue
    enabled: true,
  },
  monitoring: {
    type: 'monitoring',
    name: 'Monitoring',
    subnet: '192.168.202.0/24',
    vlanId: 202,
    gateway: '192.168.202.1',
    description: 'Provider-managed monitoring and metrics',
    accessLevel: 'provider',
    color: '#22c55e', // Green
    enabled: true,
  },
};

export function generateSegmentId(): string {
  return `seg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// ============================================
// NODE TYPES
// ============================================

export type NodeType = 'gateway' | 'compute' | 'storage' | 'daq';

export type ComputeRole = 'controller' | 'worker' | 'hybrid';

export type StorageType = 'nvme' | 'ssd' | 'hdd' | 'nas' | 'emmc';

export interface NodeSpecs {
  cpuCores: number;
  memoryGB: number;
  storageGB: number;
  powerWatts: number;
  model?: string;
  manufacturer?: string;  // Hardware manufacturer (e.g., 'Ampere', 'NVIDIA')
  description?: string;   // Brief hardware description
  // Extended hardware fields
  gpu?: GpuSpecs;
  storageType?: StorageType;
  storageVendor?: string;
  formFactorCompatibility?: FormFactor[];
  // Physical dimensions for rack visualization
  rackUnits?: number;     // Height in U (1U = 44.45mm)
  depthMm?: number;       // Depth in millimeters
  widthMm?: number;       // Width in millimeters (standard rack = 482mm)
  // GPU compatibility
  gpuSlots?: number;      // Number of discrete GPU slots (0 = no GPU support, undefined = 1)
  // Cosmetic options
  panelColor?: string;    // Custom rack panel color (hex or preset name)
}

export interface DatacenterNode {
  id: string;
  name: string;
  hostname: string;
  type: NodeType;
  ip: string;
  macAddress?: string;
  specs: NodeSpecs;
  role?: ComputeRole;
  services: string[];
  enabled: boolean;
}

export interface NodePreset {
  type: NodeType;
  count: number;
  specs: NodeSpecs;
  role?: ComputeRole;
}

// ============================================
// SERVICE TYPES
// ============================================

export type ServiceCategory = 'infrastructure' | 'storage' | 'monitoring' | 'workload' | 'ai' | 'rendering';

export interface ServiceDefinition {
  id: string;
  name: string;
  description: string;
  category: ServiceCategory;
  port: number;
  requiredNodeTypes: NodeType[];
  critical: boolean;
  defaultEnabled: boolean;
  memoryMB: number;
  cpuCores: number;
}

export interface ServiceAllocation {
  serviceId: string;
  nodeId: string;
  enabled: boolean;
  config?: Record<string, unknown>;
}

// ============================================
// SECURITY TYPES
// ============================================

export interface VPNPeer {
  id: string;
  name: string;
  publicKey: string;
  allowedIPs: string;
  enabled: boolean;
  description?: string;
}

export interface SecurityConfig {
  vpnEnabled: boolean;
  vpnListenPort: number;
  vpnSubnet: string;
  peers: VPNPeer[];
  encryptStorage: boolean;
  auditLogging: boolean;
  firewallDefaultDeny: boolean;
}

// ============================================
// FULL CONFIGURATION
// ============================================

export interface DatacenterConfig {
  // Metadata
  name: string;
  description: string;
  createdAt: number;
  updatedAt: number;

  // Scenario
  scenario: ScenarioType;
  formFactor: FormFactor;

  // Network
  network: NetworkConfig;

  // Nodes
  nodes: DatacenterNode[];

  // Services
  serviceAllocations: ServiceAllocation[];

  // Security
  security: SecurityConfig;

  // Container-specific (only when formFactor === 'container')
  containerConfig?: ContainerConfig;

  // Computed
  estimatedCost: number;
  estimatedPower: number;
}

// ============================================
// CONFIGURATOR STATE
// ============================================

export interface ConfiguratorState {
  currentStep: WizardStep;
  config: DatacenterConfig;
  isValid: boolean;
  errors: ValidationError[];
  isDirty: boolean;
}

export interface ValidationError {
  field?: string;
  message: string;
  severity: 'error' | 'warning' | 'info';
}

// ============================================
// STEP VALIDATION
// ============================================

export function isStepComplete(step: WizardStep, config: DatacenterConfig): boolean {
  // Guard against corrupted/incomplete config from localStorage
  if (!config) return false;

  switch (step) {
    case 'scenario':
      return config.scenario !== undefined;
    case 'network':
      return !!(
        config.network?.subnet &&
        config.network?.gateway &&
        config.network?.domain
      );
    case 'nodes':
      return !!(config.nodes?.length > 0 && config.nodes.some((n) => n.type === 'gateway'));
    case 'services':
      return true; // Services are optional
    case 'security':
      return true; // Security has defaults
    case 'summary':
      return true; // Terminal step
    default:
      return false;
  }
}

export function canProceedToStep(
  targetStep: WizardStep,
  config: DatacenterConfig
): boolean {
  const targetIndex = WIZARD_STEPS.indexOf(targetStep);

  for (let i = 0; i < targetIndex; i++) {
    if (!isStepComplete(WIZARD_STEPS[i], config)) {
      return false;
    }
  }

  return true;
}

// ============================================
// UTILITIES
// ============================================

export function generateNodeId(): string {
  return `node-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

export function generatePeerId(): string {
  return `peer-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

export function getNextIP(nodes: DatacenterNode[], baseIP: string): string {
  const parts = baseIP.split('.');
  const baseNum = parseInt(parts[3], 10);

  const usedIPs = new Set(nodes.map((n) => n.ip));

  for (let i = baseNum + 1; i < 255; i++) {
    const ip = `${parts[0]}.${parts[1]}.${parts[2]}.${i}`;
    if (!usedIPs.has(ip)) {
      return ip;
    }
  }

  return `${parts[0]}.${parts[1]}.${parts[2]}.${baseNum + nodes.length + 1}`;
}

export function calculateTotalPower(nodes: DatacenterNode[]): number {
  return nodes
    .filter((n) => n.enabled)
    .reduce((total, node) => total + node.specs.powerWatts, 0);
}

export function calculateTotalCost(nodes: DatacenterNode[]): number {
  // Rough cost estimation based on specs
  return nodes
    .filter((n) => n.enabled)
    .reduce((total, node) => {
      const baseCost =
        node.type === 'gateway' ? 50 :
        node.type === 'storage' ? 200 :
        node.type === 'daq' ? 150 :
        100;
      const specsCost =
        node.specs.cpuCores * 20 +
        node.specs.memoryGB * 5 +
        node.specs.storageGB * 0.5;
      const gpuCost = node.specs.gpu?.price || 0;
      return total + baseCost + specsCost + gpuCost;
    }, 0);
}

export function calculateTotalPowerWithGpu(nodes: DatacenterNode[]): number {
  return nodes
    .filter((n) => n.enabled)
    .reduce((total, node) => {
      const nodePower = node.specs.powerWatts;
      const gpuPower = node.specs.gpu?.tdpWatts || 0;
      return total + nodePower + gpuPower;
    }, 0);
}

// ============================================
// CONTAINER TYPES
// ============================================

export type ContainerSize = '20ft' | '40ft';

export interface ContainerDimensions {
  lengthMm: number;
  widthMm: number;
  heightMm: number;
}

export const CONTAINER_DIMENSIONS: Record<ContainerSize, ContainerDimensions> = {
  '20ft': { lengthMm: 5900, widthMm: 2350, heightMm: 2390 },
  '40ft': { lengthMm: 12000, widthMm: 2350, heightMm: 2390 },
};

export interface ContainerRack {
  id: string;
  name: string;
  type: 'rack-18' | 'rack-42';
  position: { x: number; y: number };  // mm from origin (front-left)
  rotation: 0 | 90 | 180 | 270;
  nodes: string[];  // Node IDs assigned to this rack
}

// ============================================
// INFRASTRUCTURE TYPES
// ============================================

export type InfrastructureType =
  | 'pdu'
  | 'ups'
  | 'cooling'
  | 'fire-suppression'
  | 'cable-tray'
  | 'monitoring'
  | 'workspace'
  | 'insulation'
  | 'structural';

export interface InfrastructureSpecs {
  powerWatts: number;
  price: number;
  widthMm: number;
  depthMm: number;
  heightMm?: number;
  rackUnits?: number;
  // PDU-specific
  outlets?: number;
  maxAmps?: number;
  inputVoltage?: number;
  // UPS-specific
  capacityVA?: number;
  runtimeMinutes?: number;
  // Cooling-specific
  btuPerHour?: number;
  cfm?: number;
  // Fire suppression
  coverageAreaM2?: number;
  // Workspace-specific
  monitorCount?: number;
  monitorSizeInch?: number;
  hasKeyboard?: boolean;
  ergonomic?: boolean;
  // Insulation-specific
  rValue?: number;           // Thermal resistance (higher = better)
  soundReductionDb?: number; // Acoustic insulation
  materialType?: 'foam' | 'mineral-wool' | 'reflective' | 'composite';
  // Structural-specific
  loadCapacityKg?: number;
  adjustable?: boolean;
  partitionType?: 'solid' | 'glass' | 'mesh' | 'modular';
  structuralType?: 'wall' | 'partition' | 'raised-floor' | 'shelf' | 'frame';
  allowsPlacement?: boolean; // Can items be placed on top (raised floor, shelf)
  heightType?: 'full' | 'half' | 'quarter'; // Wall/partition height
}

export interface InfrastructureUnit {
  id: string;
  type: InfrastructureType;
  name: string;
  specs: InfrastructureSpecs;
  position: { x: number; y: number };
  rotation?: 0 | 90 | 180 | 270; // Rotation in degrees (default 0)
  rackId?: string;       // If rack-mounted
  rackPosition?: number; // U position in rack
}

// Container insulation configuration
export interface ContainerInsulation {
  enabled: boolean;
  type: 'none' | 'basic' | 'thermal' | 'acoustic' | 'combo';
  wallRValue?: number;
  ceilingRValue?: number;
  floorRValue?: number;
  soundReductionDb?: number;
  estimatedCost: number;
}

export interface ContainerConfig {
  size: ContainerSize;
  racks: ContainerRack[];
  infrastructure: InfrastructureUnit[];
  airflowDirection: 'front-to-back' | 'side-to-side';
  insulation?: ContainerInsulation;
  gridSnapMm?: number; // Grid snap size in mm (default 100)
}

// Container stats for display
export interface ContainerStats {
  totalRackUnits: number;
  usedRackUnits: number;
  floorSpaceUsedPercent: number;
  totalPowerWatts: number;
  pduCapacityWatts: number;
  upsCapacityVA: number;
  upsRuntimeMinutes: number;
  coolingCapacityBtu: number;
  heatLoadBtu: number;
  warnings: string[];
}

// Utility functions for containers
export function generateRackId(): string {
  return `rack-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

export function generateInfrastructureId(): string {
  return `infra-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

export function getRackDimensions(type: 'rack-18' | 'rack-42'): { widthMm: number; depthMm: number; units: number } {
  return type === 'rack-42'
    ? { widthMm: 600, depthMm: 1000, units: 42 }
    : { widthMm: 600, depthMm: 600, units: 18 };
}

export function calculateContainerStats(
  containerConfig: ContainerConfig,
  nodes: DatacenterNode[]
): ContainerStats {
  // Calculate rack units
  let totalRackUnits = 0;
  let usedRackUnits = 0;

  for (const rack of containerConfig.racks) {
    const dims = getRackDimensions(rack.type);
    totalRackUnits += dims.units;

    // Calculate used units from assigned nodes
    for (const nodeId of rack.nodes) {
      const node = nodes.find(n => n.id === nodeId);
      if (node) {
        usedRackUnits += node.specs.rackUnits || 1;
      }
    }

    // Add infrastructure in this rack
    for (const infra of containerConfig.infrastructure) {
      if (infra.rackId === rack.id && infra.specs.rackUnits) {
        usedRackUnits += infra.specs.rackUnits;
      }
    }
  }

  // Calculate power
  let totalPowerWatts = 0;
  for (const nodeId of containerConfig.racks.flatMap(r => r.nodes)) {
    const node = nodes.find(n => n.id === nodeId);
    if (node && node.enabled) {
      totalPowerWatts += node.specs.powerWatts + (node.specs.gpu?.tdpWatts || 0);
    }
  }
  for (const infra of containerConfig.infrastructure) {
    totalPowerWatts += infra.specs.powerWatts;
  }

  // Calculate PDU capacity
  let pduCapacityWatts = 0;
  for (const infra of containerConfig.infrastructure) {
    if (infra.type === 'pdu' && infra.specs.maxAmps && infra.specs.inputVoltage) {
      pduCapacityWatts += infra.specs.maxAmps * infra.specs.inputVoltage;
    }
  }

  // Calculate UPS capacity and runtime
  let upsCapacityVA = 0;
  let totalUpsRuntimeMinutes = 0;
  let upsCount = 0;
  for (const infra of containerConfig.infrastructure) {
    if (infra.type === 'ups') {
      upsCapacityVA += infra.specs.capacityVA || 0;
      totalUpsRuntimeMinutes += infra.specs.runtimeMinutes || 0;
      upsCount++;
    }
  }
  const upsRuntimeMinutes = upsCount > 0 ? Math.round(totalUpsRuntimeMinutes / upsCount) : 0;

  // Calculate cooling capacity and heat load
  let coolingCapacityBtu = 0;
  for (const infra of containerConfig.infrastructure) {
    if (infra.type === 'cooling') {
      coolingCapacityBtu += infra.specs.btuPerHour || 0;
    }
  }
  const heatLoadBtu = Math.round(totalPowerWatts * 3.41); // 1W ≈ 3.41 BTU/hr

  // Calculate floor space
  const containerDims = CONTAINER_DIMENSIONS[containerConfig.size];
  const containerArea = containerDims.lengthMm * containerDims.widthMm;
  let usedArea = 0;
  for (const rack of containerConfig.racks) {
    const dims = getRackDimensions(rack.type);
    usedArea += dims.widthMm * dims.depthMm;
  }
  for (const infra of containerConfig.infrastructure) {
    if (!infra.rackId) { // Floor-mounted only
      usedArea += infra.specs.widthMm * infra.specs.depthMm;
    }
  }
  const floorSpaceUsedPercent = (usedArea / containerArea) * 100;

  // Generate warnings
  const warnings: string[] = [];
  if (pduCapacityWatts > 0 && totalPowerWatts > pduCapacityWatts) {
    warnings.push(`Power consumption (${totalPowerWatts}W) exceeds PDU capacity (${pduCapacityWatts}W)`);
  }
  if (coolingCapacityBtu > 0 && heatLoadBtu > coolingCapacityBtu) {
    warnings.push(`Heat load (${heatLoadBtu} BTU/hr) exceeds cooling capacity (${coolingCapacityBtu} BTU/hr)`);
  }
  if (containerConfig.infrastructure.filter(i => i.type === 'ups').length === 0 && totalPowerWatts > 0) {
    warnings.push('No UPS protection configured');
  }
  if (floorSpaceUsedPercent > 80) {
    warnings.push(`Floor space usage (${Math.round(floorSpaceUsedPercent)}%) exceeds recommended 80%`);
  }

  return {
    totalRackUnits,
    usedRackUnits,
    floorSpaceUsedPercent,
    totalPowerWatts,
    pduCapacityWatts,
    upsCapacityVA,
    upsRuntimeMinutes,
    coolingCapacityBtu,
    heatLoadBtu,
    warnings,
  };
}
